'use client';

import React, { useId, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import TrackedCtaLink from './TrackedCtaLink';
import SignedOutOnly from './SignedOutOnly';
import { feeGap, FEE_GAP_ANCHOR } from '@/lib/feeDrag';
import { capturePostHogEvent } from '@/analytics/posthog';

/**
 * Contextual next step under the fee table on broker and pension-provider
 * comparison pages: what a charge gap costs on the reader's own pot.
 *
 * It answers the question on the page. Replays (Oct 2026) showed readers
 * clicking "See what my fees cost", landing on the account form, and going
 * straight back to the comparison within seconds: the button promised an
 * answer and delivered a sign-up. So the sum now runs here, with the reader's
 * own numbers, and the account ask comes after it and says it is an account.
 *
 * Never names a "cheaper" provider: this is guidance, not a recommendation.
 * Illustrative maths: a lump sum, 5% a year growth before charges, no further
 * contributions, no tax.
 */
type FeeGapKind = 'broker' | 'pension';

export { FEE_GAP_ANCHOR };

const GROWTH = 0.05;
const BASE_CHARGE = 0.0025;
const GAPS = [0.001, 0.0025, 0.005] as const;

const DEFAULTS: Record<FeeGapKind, { pot: number; years: number }> = {
  broker: { pot: 50_000, years: 20 },
  pension: { pot: 100_000, years: 20 },
};

const COPY: Record<FeeGapKind, { heading: string; potLabel: string; accountBody: string }> = {
  broker: {
    heading: 'What a fee gap costs on your own pot',
    potLabel: 'Your ISA or account balance',
    accountBody:
      'That is one account. Add your ISAs and pensions to a free Delphina account and it works this out for all of them, and whether you are on track for the retirement you want.',
  },
  pension: {
    heading: 'What a charge gap does to your pension',
    potLabel: 'Your pension pot',
    accountBody:
      'That is one pot. Add all your pensions to a free Delphina account and it works this out for every one, alongside the State Pension, and whether you are on track.',
  },
};

const gbp = (n: number) =>
  n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
const pct = (n: number) => `${(n * 100).toFixed(2).replace(/0$/, '')}%`;

/** Parse a typed amount like "£120,000" or "120k". Returns NaN when it is not a number. */
export function parseAmount(raw: string): number {
  const s = raw.trim().toLowerCase().replace(/[£,\s]/g, '');
  const m = s.match(/^(\d+(?:\.\d+)?)(k|m)?$/);
  if (!m) return NaN;
  const mult = m[2] === 'm' ? 1_000_000 : m[2] === 'k' ? 1_000 : 1;
  return Number(m[1]) * mult;
}

export default function FeeGapCard({
  kind,
  pairSlug,
  nameA,
  nameB,
}: {
  kind: FeeGapKind;
  pairSlug: string;
  nameA: string;
  nameB: string;
}) {
  const id = useId();
  const copy = COPY[kind];
  const [potInput, setPotInput] = useState(DEFAULTS[kind].pot.toLocaleString('en-GB'));
  const [yearsInput, setYearsInput] = useState(String(DEFAULTS[kind].years));
  const [gap, setGap] = useState<number>(0.0025);
  const reported = useRef(false);

  const pot = parseAmount(potInput);
  const potValid = Number.isFinite(pot) && pot > 0 && pot <= 10_000_000;
  const years = Number(yearsInput);
  const yearsValid = Number.isInteger(years) && years >= 1 && years <= 50;
  const valid = potValid && yearsValid;
  const cost = valid ? feeGap(pot, GROWTH, BASE_CHARGE, BASE_CHARGE + gap, years) : 0;

  // One event per page view, on the first change, so the funnel can count
  // readers who used the calculator without counting every keystroke.
  const noteUse = () => {
    if (reported.current) return;
    reported.current = true;
    capturePostHogEvent('fee_gap_calculated', { kind, page_pair: pairSlug });
  };

  const href = `/login?ref=${kind}-fee-card-${pairSlug}`;

  return (
    <section
      id={FEE_GAP_ANCHOR}
      aria-labelledby={`${id}-heading`}
      className="scroll-mt-24 bg-positive-50 border-2 border-black shadow-brutal p-6 sm:p-8"
    >
      <h3 id={`${id}-heading`} className="text-2xl font-bold mb-2 text-ink">
        {copy.heading}
      </h3>
      <p className="text-gray-700 mb-6">Put in your own numbers. Nothing is saved and there is no sign-up.</p>

      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label htmlFor={`${id}-pot`} className="block text-sm font-semibold mb-1">
            {copy.potLabel}
          </label>
          <input
            id={`${id}-pot`}
            inputMode="decimal"
            autoComplete="off"
            value={potInput}
            onChange={e => {
              setPotInput(e.target.value);
              noteUse();
            }}
            aria-invalid={!potValid}
            aria-describedby={!potValid ? `${id}-pot-error` : undefined}
            className="w-full min-h-[44px] border-2 border-black bg-white px-3 text-lg"
          />
          {!potValid && (
            <p id={`${id}-pot-error`} className="mt-1 text-sm text-danger-700">
              Enter an amount in pounds, for example 75,000.
            </p>
          )}
        </div>
        <div>
          <label htmlFor={`${id}-years`} className="block text-sm font-semibold mb-1">
            Years until you need it
          </label>
          <input
            id={`${id}-years`}
            type="number"
            inputMode="numeric"
            min={1}
            max={50}
            value={yearsInput}
            onChange={e => {
              setYearsInput(e.target.value);
              noteUse();
            }}
            aria-invalid={!yearsValid}
            className="w-full min-h-[44px] border-2 border-black bg-white px-3 text-lg"
          />
        </div>
        <fieldset>
          <legend className="block text-sm font-semibold mb-1">Difference in yearly charges</legend>
          <div className="flex gap-2">
            {GAPS.map(g => (
              <label
                key={g}
                className="flex-1 min-h-[44px] flex items-center justify-center border-2 border-black bg-white cursor-pointer has-[:checked]:bg-ink has-[:checked]:text-white font-semibold has-[:focus-visible]:ring-4 has-[:focus-visible]:ring-warning-400"
              >
                <input
                  type="radio"
                  name={`${id}-gap`}
                  value={g}
                  checked={gap === g}
                  onChange={() => {
                    setGap(g);
                    noteUse();
                  }}
                  className="sr-only"
                />
                {pct(g)}
              </label>
            ))}
          </div>
        </fieldset>
      </div>

      <div aria-live="polite" className="mt-6 border-2 border-black bg-white p-4 sm:p-5">
        {valid ? (
          <p className="text-lg text-ink">
            Over {years} {years === 1 ? 'year' : 'years'}, paying {pct(gap)} a year more in charges on{' '}
            {gbp(pot)} leaves you about <strong className="text-2xl">{gbp(cost)}</strong> worse off.
          </p>
        ) : (
          <p className="text-gray-600">Enter your balance and a number of years from 1 to 50 to see the figure.</p>
        )}
      </div>

      <SignedOutOnly>
        <p className="mt-6 text-ink">{copy.accountBody}</p>
        <TrackedCtaLink
          href={href}
          position="inline"
          ctaText="Create a free account"
          variant={`${kind}-fee-card`}
          location="provider_comparison"
          pagePair={pairSlug}
          className="mt-4 inline-flex items-center gap-2 bg-positive-700 hover:bg-positive-800 text-white font-bold py-3 px-6 rounded-md border-2 border-ink shadow-brutal-sm hover:shadow-none hover:translate-x-0.5 hover:translate-y-0.5 transition-all"
        >
          Create a free account
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </TrackedCtaLink>
        <p className="mt-2 text-sm text-gray-600">Free plan, no card. About two minutes.</p>
      </SignedOutOnly>

      <p className="mt-4 text-sm text-gray-700">
        Illustrative: a lump sum growing at 5% a year before charges ({pct(BASE_CHARGE)} vs{' '}
        {pct(BASE_CHARGE + gap)} a year), no further contributions, no tax. Not a recommendation of {nameA} or{' '}
        {nameB}. Delphina is guidance, not financial advice.
      </p>
    </section>
  );
}
